import React, { useCallback, useEffect, useState } from 'react';
import Modal from '#/common/components/Modal';
import TMMButton from '#/common/components/TMMButton';
import FileInfoCard from '#/common/components/FileUpload/FileInfoCard';
import { Attachment, ImportFileOutputData } from '#/graphql/types';
import getAttachmentQuery from '#/common/components/AttachmentUpload/hooks//useAttachmentQuery';

export interface SuccessModalBaseProps {
  visible: boolean;
  onHiding: () => void;
  importResult: ImportFileOutputData | null;
  /** Lowercase "X records" label, e.g. "asset records" / "finding records" / "work order records". */
  entityRecordsLabelLower: string;
  /** Lowercase plural entity label, e.g. "assets" / "findings" / "work orders". */
  entityPluralLabelLower: string;
  /** Called when the user dismisses the modal (Skip or Download File) to redirect the user. */
  onRedirect: () => void;
}

/**
 * Shared "Successful Import" summary modal for every data-import flow (Asset, Work Order,
 * Finding, ...). Fetches the generated error file (if any) and lets the user download it.
 * Entity-specific redirect/copy is supplied by the caller.
 */
const SuccessModalBase: React.FC<SuccessModalBaseProps> = ({
  visible,
  onHiding,
  importResult,
  entityRecordsLabelLower,
  entityPluralLabelLower,
  onRedirect,
}) => {
  const { getAttachment } = getAttachmentQuery();
  const [attachmentData, setAttachmentData] = useState<Attachment | undefined>(undefined);

  useEffect(() => {
    const fetchAttachment = async () => {
      if (!importResult?.errorFileId) {
        setAttachmentData(undefined);
        return;
      }

      const resp: any = await getAttachment(importResult.errorFileId);
      setAttachmentData(resp?.data?.getAttachment?.data as Attachment | undefined);
    };

    fetchAttachment();
  }, [importResult, getAttachment]);

  const handleDownloadFile = useCallback(async () => {
    if (attachmentData?.downloadUrl) {
      window.open(attachmentData?.downloadUrl, '_blank');
    }
    onRedirect();
  }, [attachmentData, onRedirect]);

  const createdCount = importResult?.successfulImports ?? 0;
  const failedCount = importResult ? importResult.totalRows - importResult.successfulImports : 0;
  const totalCount = importResult?.totalRows ?? 0;
  const hasFailures = failedCount > 0;

  return (
    <Modal
      visible={visible}
      title={hasFailures ? 'Import Report' : 'Successful Import'}
      width={640}
      height="auto"
      onHiding={onHiding}
      // Keep a single stable wrapper `<div>` here - `content` renders as a direct child of a
      // DevExtreme ScrollView, which breaks if its direct descendant shape changes.
      content={
        <div>
          {hasFailures ? (
            <>
              <div style={{ marginBottom: '16px', marginTop: '16px' }}>
                {createdCount}/{totalCount} New {entityRecordsLabelLower} were created.
              </div>
              <div style={{ marginBottom: '20px' }}>
                We have found issues in {failedCount}/{totalCount} {entityRecordsLabelLower} that
                need to be fixed before importing them again. Here is a file with the{' '}
                {entityPluralLabelLower} and their errors.
              </div>
              <FileInfoCard
                name={attachmentData?.originalFileName ?? 'output.csv'}
                size={attachmentData?.size ?? 0}
                width="100%"
              />
            </>
          ) : (
            <div style={{ marginBottom: '16px', marginTop: '16px' }}>
              {createdCount}/{totalCount} New {entityRecordsLabelLower} were created successfully.
            </div>
          )}
        </div>
      }
      actions={
        hasFailures
          ? [
              <TMMButton key="cancel" label="Skip" type="normal" onClick={onRedirect} />,
              <TMMButton
                key="ok"
                label="Download File"
                type="submit"
                onClick={handleDownloadFile}
              />,
            ]
          : [<TMMButton key="ok" label="OK" type="submit" onClick={onRedirect} />]
      }
    />
  );
};

export default React.memo(SuccessModalBase);
